import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../AuthContext';
import { Plus, Trash2, Edit2, Video, Loader2, X, ExternalLink, Eye, EyeOff, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', video_url: '', is_public: false, video_type: 'url' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [learningVisible, setLearningVisible] = useState(false);
  const [allChannelsVisible, setAllChannelsVisible] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [togglingChannels, setTogglingChannels] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVideos = async () => {
    try {
      const res = await apiCall('/api/videos', { method: 'GET' });
      setVideos(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await apiCall('/api/app-settings', { method: 'GET' });
      setLearningVisible(!!res.data?.learning_videos_visible);
      setAllChannelsVisible(!!res.data?.all_sales_channels_visible);
    } catch {}
  };

  useEffect(() => { fetchVideos(); fetchSettings(); }, []);

  const toggleLearningVisibility = async () => {
    setToggling(true);
    try {
      const newVal = !learningVisible;
      await apiCall('/api/app-settings', { method: 'PUT', data: { learning_videos_visible: newVal } });
      setLearningVisible(newVal);
    } catch (e) { alert('Failed to update visibility'); } finally { setToggling(false); }
  };

  const toggleAllChannelsVisibility = async () => {
    setTogglingChannels(true);
    try {
      const newVal = !allChannelsVisible;
      await apiCall('/api/app-settings', { method: 'PUT', data: { all_sales_channels_visible: newVal } });
      setAllChannelsVisible(newVal);
    } catch (e) { alert('Failed to update'); } finally { setTogglingChannels(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadProgress(0);
    try {
      // Auth: include httpOnly cookie via credentials:'include'.
      // Bearer header kept as fallback for non-cookie sessions.
      const token = localStorage.getItem('access_token') || document.cookie.match(/access_token=([^;]+)/)?.[1] || '';
      const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
      const fetchOpts = { credentials: 'include' };
      // 1. Initialize upload session
      const startRes = await fetch(`${API}/api/upload-video/start`, {
        ...fetchOpts,
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name }),
      });
      if (!startRes.ok) throw new Error(`Init failed: ${startRes.status} ${await startRes.text()}`);
      const { upload_id } = await startRes.json();
      // 2. Stream file in 512KB chunks
      const CHUNK_SIZE = 512 * 1024;
      let uploaded = 0;
      let index = 0;
      while (uploaded < file.size) {
        const slice = file.slice(uploaded, Math.min(uploaded + CHUNK_SIZE, file.size));
        const fd = new FormData();
        fd.append('upload_id', upload_id);
        fd.append('chunk', slice, `chunk-${index}`);
        const r = await fetch(`${API}/api/upload-video/chunk`, {
          ...fetchOpts, method: 'POST', headers: authHeader, body: fd,
        });
        if (!r.ok) throw new Error(`Chunk ${index} failed: ${r.status}`);
        uploaded += slice.size;
        index += 1;
        setUploadProgress(Math.round((uploaded / file.size) * 100));
      }
      // 3. Complete
      const doneRes = await fetch(`${API}/api/upload-video/complete`, {
        ...fetchOpts,
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id }),
      });
      if (!doneRes.ok) throw new Error(`Complete failed: ${doneRes.status} ${await doneRes.text()}`);
      const data = await doneRes.json();
      setForm(p => ({ ...p, video_url: data.video_url, video_type: 'upload' }));
    } catch (err) {
      alert('Upload error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.video_type === 'upload' && !form.video_url) {
      alert('Please finish uploading the video file before saving.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await apiCall(`/api/videos/${editing}`, { method: 'PUT', data: form });
      } else {
        await apiCall('/api/videos', { method: 'POST', data: form });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', description: '', video_url: '', is_public: false, video_type: 'url' });
      fetchVideos();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving video');
    } finally { setSaving(false); }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await apiCall(`/api/videos/${videoId}`, { method: 'DELETE' });
      fetchVideos();
    } catch { alert('Failed to delete'); }
  };

  const openEdit = (video) => {
    setEditing(video.id);
    setForm({ title: video.title, description: video.description || '', video_url: video.video_url, is_public: video.is_public, video_type: video.video_type || 'url' });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-videos-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Video Management</h2>
          <p className="text-sm text-slate-500 mt-1">Manage learning and tutorial videos for your users</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', description: '', video_url: '', is_public: false, video_type: 'url' }); }}
          className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
          data-testid="create-video-button">
          <Plus className="w-4 h-4" /> New Video
        </button>
      </div>

      {/* Visibility toggle */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between shadow-sm" data-testid="learning-visibility-card">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Learning Videos page visible to customers</p>
          <p className="text-xs text-slate-500 mt-0.5">When OFF, the "Learning Videos" menu is hidden from regular users. Admin always has access.</p>
        </div>
        <button
          onClick={toggleLearningVisibility}
          disabled={toggling}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${learningVisible ? 'bg-[#10B981]' : 'bg-slate-300'} disabled:opacity-50`}
          data-testid="learning-visibility-toggle"
          aria-pressed={learningVisible}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${learningVisible ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Sales channels visibility toggle */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between shadow-sm" data-testid="all-channels-visibility-card">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Show all Shopify Sales Channels</p>
          <p className="text-xs text-slate-500 mt-0.5 max-w-xl">When OFF (default), only Online Store and Point of Sale appear in the Product Form. Turn ON after Shopify approves the <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">read_publications</code> / <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">write_publications</code> scopes to enable Google, Facebook, TikTok, etc.</p>
        </div>
        <button
          onClick={toggleAllChannelsVisibility}
          disabled={togglingChannels}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${allChannelsVisible ? 'bg-[#10B981]' : 'bg-slate-300'} disabled:opacity-50`}
          data-testid="all-channels-visibility-toggle"
          aria-pressed={allChannelsVisible}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allChannelsVisible ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="video-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A]">{editing ? 'Edit Video' : 'Add Video'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Title</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="Video title" data-testid="video-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm resize-none"
                  placeholder="Brief description..." data-testid="video-desc-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Video Source</label>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setForm(p => ({ ...p, video_type: 'url' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${form.video_type === 'url' ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid="video-source-url">URL (YouTube/Vimeo)</button>
                  <button type="button" onClick={() => setForm(p => ({ ...p, video_type: 'upload' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${form.video_type === 'upload' ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid="video-source-upload">Upload File</button>
                </div>
                {form.video_type === 'url' ? (
                  <div className="space-y-2">
                    <input value={form.video_url} onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                      placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…" data-testid="video-url-input" />
                    <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800 leading-relaxed">
                      <strong className="font-bold">✓ Recommended.</strong> Upload your video to <a href="https://youtube.com/upload" target="_blank" rel="noreferrer" className="underline font-semibold">YouTube</a> (set as <em>Unlisted</em> if you don't want it public) or <a href="https://vimeo.com/upload" target="_blank" rel="noreferrer" className="underline font-semibold">Vimeo</a>, then paste the link here. Free, permanent, faster playback worldwide.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="px-3 py-2 mb-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 leading-relaxed">
                      <strong className="font-bold">⚠ Warning:</strong> Uploaded files are <strong>deleted on every redeploy</strong>. For permanent storage, switch to "URL (YouTube/Vimeo)" above.
                    </div>
                    {form.video_url && form.video_type === 'upload' ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 mb-2">
                        <Eye className="w-4 h-4" /> Video uploaded: {form.video_url.split('/').pop()}
                      </div>
                    ) : null}
                    <input type="file" ref={fileInputRef} accept="video/*" onChange={handleFileUpload} className="hidden" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 flex flex-col items-center gap-2 text-slate-500 hover:border-[#0052FF] hover:text-[#0052FF] transition-colors disabled:opacity-50"
                      data-testid="video-upload-btn">
                      {uploading ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span className="text-sm font-medium">Uploading... {uploadProgress}%</span>
                          <div className="w-48 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0052FF] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6" />
                          <span className="text-sm font-medium">Click to upload video</span>
                          <span className="text-xs text-slate-400">MP4, WebM, MOV, AVI (max recommended: 500MB)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="video-public-toggle">
                <div className={`relative w-10 h-6 rounded-full transition-colors ${form.is_public ? 'bg-[#0052FF]' : 'bg-slate-200'}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_public ? 'translate-x-5' : 'translate-x-1'}`} />
                </div>
                <input type="checkbox" checked={form.is_public} onChange={e => setForm(p => ({ ...p, is_public: e.target.checked }))} className="sr-only" />
                <span className="text-sm font-medium text-[#0F172A]">Public (visible on landing page)</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 py-2.5 rounded-lg font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="video-submit-button"
                  className="flex-1 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Video List */}
      {videos.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-videos">
          <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No videos yet</p>
          <p className="text-sm text-slate-400 mt-1">Add learning videos for your users</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="video-grid">
          {videos.map(video => (
            <div key={video.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
              data-testid={`video-card-${video.id}`}>
              <div className="aspect-video bg-slate-100 flex items-center justify-center relative">
                <Video className="w-10 h-10 text-slate-300" />
                <div className="absolute top-2 right-2">
                  {video.is_public ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <Eye className="w-3 h-3" /> Public
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                      <EyeOff className="w-3 h-3" /> Private
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h4 className="text-sm font-semibold text-[#0F172A] mb-1 truncate">{video.title}</h4>
                <p className="text-xs text-slate-500 line-clamp-2 mb-3">{video.description || 'No description'}</p>
                <div className="flex items-center gap-2">
                  <a href={video.video_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:text-[#0040CC] font-medium">
                    <ExternalLink className="w-3 h-3" /> Open
                  </a>
                  <span className="text-slate-200">|</span>
                  <button onClick={() => openEdit(video)} className="text-xs text-slate-500 hover:text-[#0052FF] font-medium"
                    data-testid={`edit-video-${video.id}`}>Edit</button>
                  <span className="text-slate-200">|</span>
                  <button onClick={() => handleDelete(video.id)} className="text-xs text-slate-500 hover:text-red-500 font-medium"
                    data-testid={`delete-video-${video.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
